import type { FC, FormEvent, ReactNode } from 'react'
import type { OrderFields } from '@/components/order/order-summary'
import type {
    CartLine,
    MenuCategory,
    MenuItem,
    PickupAvailability,
    PlacedOrder
} from '@/types'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ShoppingBag, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { revealDelay } from '@/lib/motion'
import { formatMoney, formatNumber, joinNames, pluralize, toLatinDigits } from '@/lib/format'
import { usePresence } from '@/hooks/use-presence'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll'
import { useCart } from '@/components/order/cart-provider'
import { OrderSummary } from '@/components/order/order-summary'
import { OrderItemList } from '@/components/order/order-item-list'
import { OrderConfirmation } from '@/components/order/order-confirmation'
import { Button } from '@/components/generals/button'
import { SectionHeading } from '@/components/generals/section-heading'

import { ORDER_RULES, SHELL } from '@/constants/site'

interface OrderBuilderProps {
    categories: Array<MenuCategory>
    items: Array<MenuItem>
}

type AvailabilityState = 'loading' | 'ready' | 'error'

interface ErrorPayload {
    message?: string
    code?: string
    errors?: Record<string, string>
    slugs?: Array<string>
}

const EMPTY_FIELDS: OrderFields = { name: '', phone: '', note: '', website: '' }

const phoneDigits = (value: string): number => toLatinDigits(value).replace(/\D/g, '').length

export const OrderBuilder: FC<OrderBuilderProps> = ({ categories, items }) => {
    const { lines, total, count, hydrated, setQuantity, remove, replace, clear } = useCart()
    const [availability, setAvailability] = useState<PickupAvailability | null>(null)
    const [availabilityState, setAvailabilityState] = useState<AvailabilityState>('loading')
    const [date, setDate] = useState<string | null>(null)
    const [time, setTime] = useState<string | null>(null)
    const [fields, setFields] = useState<OrderFields>(EMPTY_FIELDS)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [placed, setPlaced] = useState<PlacedOrder | null>(null)
    const [sheetOpen, setSheetOpen] = useState(false)

    const itemsBySlug = useMemo(() => new Map(items.map(item => [item.slug, item])), [items])
    const quantities = useMemo(() => new Map(lines.map(line => [line.slug, line.quantity])), [lines])

    // Brings anything saved in the basket up to date with today's menu and prices.
    useEffect(() => {
        if (!hydrated || itemsBySlug.size === 0) return

        const kept: Array<CartLine> = []
        const dropped: Array<string> = []
        let changed = false

        for (const line of lines) {
            const item = itemsBySlug.get(line.slug)

            if (!item) {
                dropped.push(line.name)
                changed = true

                continue
            }

            if (item.price !== line.price || item.name !== line.name || item.image !== line.image) changed = true

            kept.push({ ...line, name: item.name, price: item.price, image: item.image })
        }

        if (changed) replace(kept)

        if (dropped.length > 0) setNotice(`${joinNames(dropped)} دیگر قابل پیش‌سفارش ${dropped.length > 1 ? 'نیستند' : 'نیست'}، برای همین ${dropped.length > 1 ? 'آن‌ها را' : 'آن را'} از سبد سفارشتان برداشتیم.`)
        // Runs once the saved basket has loaded, not on every change to it.
    }, [hydrated, itemsBySlug, replace])

    const loadAvailability = useCallback(async (): Promise<PickupAvailability | null> => {
        setAvailabilityState('loading')

        try {
            const response = await fetch('/api/order/availability', { cache: 'no-store' })

            if (!response.ok) throw new Error('Availability request failed.')

            const data = await response.json() as PickupAvailability

            setAvailability(data)
            setAvailabilityState('ready')
            setDate(current => data.days.some(day => day.date === current && day.status === 'open') ? current : data.days.find(day => day.status === 'open')?.date ?? null)

            return data
        }
        catch {
            setAvailabilityState('error')

            return null
        }
    }, [])

    useEffect(() => {
        void loadAvailability()

        const interval = window.setInterval(() => void loadAvailability(), 3 * 60 * 1000)

        return () => window.clearInterval(interval)
    }, [loadAvailability])

    // A chosen time that is no longer offered (taken, or too soon now) is cleared.
    useEffect(() => {
        if (!availability || !time) return

        const day = availability.days.find(entry => entry.date === date)

        if (!day?.slots.some(slot => slot.time === time)) setTime(null)
    }, [availability, date, time])

    const todayConflicts = useMemo(() => {
        if (!availability || date !== availability.today) return []

        return lines
            .map(line => itemsBySlug.get(line.slug))
            .filter((item): item is MenuItem => Boolean(item) && (item?.status === null || item?.status === 'sold-out'))
    }, [availability, date, lines, itemsBySlug])

    const warning = todayConflicts.length > 0
        ? `${joinNames(todayConflicts.map(item => item.name))} امروز موجود ${todayConflicts.length > 1 ? 'نیستند' : 'نیست'}. روز دیگری را انتخاب کنید، یا ${todayConflicts.length > 1 ? 'آن‌ها را' : 'آن را'} از سفارشتان بردارید.`
        : null

    const clearError = (key: string): void => {
        if (!errors[key]) return

        setErrors(current => {
            const next = { ...current }

            delete next[key]

            return next
        })
    }

    const onField = (field: keyof OrderFields, value: string): void => {
        setFields(current => ({ ...current, [field]: value }))
        clearError(field === 'name' || field === 'phone' ? field : '')
    }

    const onQuantity = (item: Pick<MenuItem, 'slug' | 'name' | 'price' | 'image'>, quantity: number): void => {
        setQuantity(item, quantity)
        clearError('items')
        setFormError(null)
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const form = event.currentTarget
        const nextErrors: Record<string, string> = {}
        const pieces = lines.reduce((sum, line) => sum + line.quantity, 0)

        if (lines.length === 0) nextErrors.items = 'سبد سفارش شما خالی است. اول چیزی از فهرست اضافه کنید.'
        else if (pieces > ORDER_RULES.maxPiecesPerOrder) nextErrors.items = `سفارش آنلاین حداکثر ${formatNumber(ORDER_RULES.maxPiecesPerOrder)} عدد است. برای سفارش‌های بزرگ‌تر، لطفاً با ما تماس بگیرید.`
        else if (warning) nextErrors.items = warning

        if (!date) nextErrors.pickupDate = 'لطفاً روز تحویل را انتخاب کنید.'
        if (!time) nextErrors.pickupTime = 'لطفاً زمان تحویل را انتخاب کنید.'
        if (fields.name.trim().length < 2) nextErrors.name = 'لطفاً بگویید سفارش به چه نامی ثبت شود.'

        const digits = phoneDigits(fields.phone)

        if (digits < 7 || digits > 15) nextErrors.phone = 'لطفاً شماره تلفنی وارد کنید که بتوانیم با آن با شما تماس بگیریم.'

        setErrors(nextErrors)
        setFormError(null)

        if (Object.keys(nextErrors).length > 0) {
            const firstField = nextErrors.name ? 'name' : nextErrors.phone ? 'phone' : null

            if (firstField) form.querySelector<HTMLInputElement>(`[name="${firstField}"]`)?.focus()

            return
        }

        setSubmitting(true)

        try {
            const response = await fetch('/api/order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: lines.map(line => ({ slug: line.slug, quantity: line.quantity })),
                    pickupDate: date,
                    pickupTime: time,
                    name: fields.name.trim(),
                    phone: fields.phone.trim(),
                    note: fields.note.trim(),
                    website: fields.website
                })
            })

            const payload = await response.json().catch(() => null) as (PlacedOrder & ErrorPayload) | null

            if (response.ok && payload?.reference) {
                setPlaced(payload)
                clear()
                setSheetOpen(false)
                setFields(EMPTY_FIELDS)
                setTime(null)
                window.scrollTo({ top: 0 })

                return
            }

            if (response.status === 400 && payload?.errors) {
                setErrors(payload.errors)
                setFormError(payload.message ?? null)

                return
            }

            if (response.status === 409 && payload?.code) {
                if (payload.code === 'slot-full' || payload.code === 'slot-unavailable') {
                    setTime(null)
                    setErrors({ pickupTime: payload.message ?? 'این زمان دیگر در دسترس نیست. لطفاً زمان دیگری انتخاب کنید.' })
                    void loadAvailability()

                    return
                }

                if (payload.code === 'items-unavailable') {
                    payload.slugs?.forEach(slug => remove(slug))
                    setFormError(payload.message ?? null)

                    return
                }

                if (payload.code === 'not-today') {
                    setErrors({ items: payload.message ?? 'برخی اقلام امروز موجود نیستند.' })

                    return
                }
            }

            setFormError(payload?.message ?? 'همین حالا نتوانستیم سفارشتان را ثبت کنیم و هیچ مبلغی هم از شما کسر نشده است. لطفاً چند لحظه‌ی دیگر دوباره امتحان کنید یا با ما تماس بگیرید.')
        }
        catch {
            setFormError('همین حالا نتوانستیم به نانوایی وصل شویم. لطفاً اتصال اینترنت خود را بررسی کنید و دوباره امتحان کنید.')
        }
        finally {
            setSubmitting(false)
        }
    }

    const startAgain = (): void => {
        setPlaced(null)
        setErrors({})
        setFormError(null)
        void loadAvailability()
        window.scrollTo({ top: 0 })
    }

    const summaryProps = {
        lines,
        total,
        onQuantity: (line: CartLine, quantity: number) => onQuantity(line, quantity),
        onRemove: (slug: string) => {
            remove(slug)
            clearError('items')
        },
        availability,
        availabilityState,
        date,
        time,
        onDateChange: (next: string) => {
            setDate(next)
            setTime(null)
            clearError('pickupDate')
            clearError('items')
        },
        onTimeChange: (next: string) => {
            setTime(next)
            clearError('pickupTime')
        },
        onRetry: () => void loadAvailability(),
        fields,
        onField,
        errors,
        formError,
        warning,
        submitting,
        onSubmit
    }

    if (placed) return <OrderConfirmation order={placed} onStartAgain={startAgain} />

    return (
        <>
            <div className={cn(SHELL, 'pt-12 pb-10 lg:pt-20 lg:pb-14')}>
                <SectionHeading
                    as='h1'
                    label='پیش‌سفارش'
                    title={<>از قبل سفارش دهید، <span className='italic'>حضوری</span> تحویل بگیرید</>}
                    intro={`نان و شیرینی‌تان را انتخاب کنید، زمان تحویل را برگزینید و هنگام تحویل پرداخت کنید. پیش‌سفارش باید دست‌کم دو ساعت زودتر ثبت شود و تا ${formatNumber(ORDER_RULES.daysAhead)} روز آینده امکان‌پذیر است.`}
                />
            </div>

            <div className={cn(SHELL, 'grid items-start gap-12 pb-16 lg:grid-cols-12 lg:gap-10 lg:pb-32')}>
                <div className='lg:col-span-7' data-reveal='up' style={revealDelay(200)}>
                    {notice && (
                        <p className='mb-8 flex items-start gap-3 border-s-2 border-amber ps-4 text-[0.9375rem] leading-relaxed' role='status'>
                            <span className='flex-1'>{notice}</span>
                            <button type='button' onClick={() => setNotice(null)} aria-label='بستن پیام' className='inline-flex size-8 shrink-0 cursor-pointer items-center justify-center text-crust-soft hover:text-foreground'>
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        </p>
                    )}

                    {items.length === 0 ? (
                        <div className='border-y border-line py-14 text-center'>
                            <p className='font-heading text-3xl'>پیش‌سفارش برای مدت کوتاهی متوقف شده است.</p>
                            <p className='mx-auto mt-3 max-w-md text-crust-soft'>لطفاً کمی بعد دوباره سر بزنید، یا با ما تماس بگیرید تا چیزی برایتان کنار بگذاریم.</p>
                        </div>
                    ) : (
                        <OrderItemList categories={categories} items={items} quantities={quantities} onChange={onQuantity} />
                    )}
                </div>

                <aside className='hidden lg:sticky lg:top-[calc(var(--header-offset)+1.5rem)] lg:col-span-5 lg:block lg:transition-[top] lg:duration-500' aria-label='خلاصه‌ی سفارش'>
                    <div className='max-h-[calc(100svh-var(--header-offset)-3rem)] overflow-y-auto overscroll-contain border border-line bg-popover p-7 xl:p-9'>
                        <OrderSummary idPrefix='order' {...summaryProps} />
                    </div>
                </aside>
            </div>

            <div className='sticky bottom-0 z-40 border-t border-line bg-background/95 backdrop-blur-sm lg:hidden'>
                <div className={cn(SHELL, 'flex items-center gap-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]')}>
                    <div className='min-w-0 flex-1' aria-live='polite'>
                        <p className='flex items-center gap-2 text-sm font-medium'>
                            <ShoppingBag className='size-4 text-crust-soft' aria-hidden='true' />
                            {hydrated && count > 0 ? pluralize(count, 'عدد') : 'سبد سفارش خالی است'}
                        </p>

                        <p className='text-xs text-crust-soft tabular-nums'>
                            {hydrated && count > 0 ? `${formatMoney(total)} · پرداخت هنگام تحویل` : 'برای شروع چیزی اضافه کنید'}
                        </p>
                    </div>

                    <Button onClick={() => setSheetOpen(true)} disabled={!hydrated || count === 0} aria-haspopup='dialog'>
                        مرور سفارش
                    </Button>
                </div>
            </div>

            <MobileOrderSheet open={sheetOpen} onClose={() => setSheetOpen(false)}>
                <OrderSummary idPrefix='sheet' showTitle={false} {...summaryProps} />
            </MobileOrderSheet>
        </>
    )
}

interface MobileOrderSheetProps {
    open: boolean
    onClose: () => void
    children: ReactNode
}

const MobileOrderSheet: FC<MobileOrderSheetProps> = ({ open, onClose, children }) => {
    const panelRef = useRef<HTMLDivElement>(null)
    const { mounted, state } = usePresence(open, 320)

    useLockBodyScroll(open)
    useFocusTrap(panelRef, open && mounted, onClose)

    if (!mounted) return null

    return (
        <div className='fixed inset-0 z-50 lg:hidden'>
            <div className='ld-overlay absolute inset-0 bg-crust/45' data-state={state} onClick={onClose} aria-hidden='true' />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby='order-sheet-title'
                tabIndex={-1}
                data-state={state}
                className='ld-sheet-full absolute inset-x-0 bottom-0 flex max-h-[94svh] flex-col bg-background outline-none'
            >
                <div className='flex h-16 shrink-0 items-center justify-between border-b border-line px-5 sm:px-8'>
                    <h2 id='order-sheet-title' className='font-heading text-[1.75rem] leading-none font-normal'>سفارش شما</h2>

                    <button
                        type='button'
                        onClick={onClose}
                        className='-me-2 inline-flex h-11 cursor-pointer items-center gap-2 px-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-amber'
                    >
                        بستن
                        <X className='size-5' aria-hidden='true' />
                    </button>
                </div>

                <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-2 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-8'>
                    {children}
                </div>
            </div>
        </div>
    )
}
